'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  role?: string;
  avatar_url?: string;
  created_at?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  userProfile: UserProfile | null;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  enableDemoMode: (role: string) => void;
  isDemoMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const supabase = createClient();

  // Fetch user profile from database
  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      setUserProfile(data);
      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
      return null;
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
      
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Helper: Wait for profile to exist with retry
  const waitForProfile = async (userId: string, maxAttempts = 10): Promise<UserProfile | null> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`🔍 Checking for profile (attempt ${attempt}/${maxAttempts})...`);
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (data && !error) {
        console.log('✅ Profile found:', data);
        return data;
      }
      
      if (attempt < maxAttempts) {
        // Wait 500ms between attempts (total max wait: 5 seconds)
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    console.error('❌ Profile not found after', maxAttempts, 'attempts');
    return null;
  };

  // Email/Password Sign Up - UPDATED to directly create profile after auth
  const signUp = async (email: string, password: string, metadata = {}) => {
    try {
      console.log('🔵 STEP 1: Creating auth user...');
      
      // Step 1: Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password
      });

      if (authError) {
        console.error('❌ Auth signup error:', authError);
        
        // Handle specific rate limit errors
        if (authError.message?.includes('email rate limit exceeded')) {
          throw new Error('Email rate limit exceeded. Supabase has temporarily limited email confirmations due to too many attempts. Please wait 1 hour before trying again.');
        }
        if (authError.message?.includes('rate limit')) {
          throw new Error('Too many requests. Please wait a few minutes and try again.');
        }
        
        throw authError;
      }
      
      if (!authData.user) {
        console.error('❌ No user returned from auth');
        throw new Error('User creation failed - no user data returned');
      }

      console.log('✅ Auth user created:', authData.user.id);
      console.log('🔵 STEP 2: Creating user profile with RLS policy...');

      // Step 2: Create user_profiles row
      // RLS policy now allows authenticated users to INSERT their own profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          id: authData.user.id,
          email: email,
          full_name: metadata?.fullName || '',
          role: metadata?.role || 'online-customer',
          phone: metadata?.phoneNumber || null,
          whatsapp_number: metadata?.whatsappNumber || null,
          street: metadata?.street || null,
          city: metadata?.city || null,
          emirate_or_state: metadata?.emirateOrState || null,
          country: metadata?.country || null,
          zip_code: metadata?.zipCode || null,
          receive_promotions: metadata?.receivePromotions || false,
          receive_recommendations: metadata?.receiveRecommendations || false
        })
        .select()
        .single();

      if (profileError) {
        console.error('❌ Profile creation error:', profileError);
        throw new Error(`Profile creation failed: ${profileError.message}`);
      }

      console.log('✅ Profile created:', profileData);
      setUserProfile(profileData);

      return authData;
    } catch (error: any) {
      console.error('❌ SignUp error:', error);
      throw error;
    }
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    
    // Fetch profile after signin
    if (data.user) {
      await fetchUserProfile(data.user.id);
    }
    
    return data;
  };

  // Sign Out
  const signOut = async () => {
    if (isDemoMode) {
      setIsDemoMode(false);
      setUser(null);
      setUserProfile(null);
      return;
    }
    
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    
    // Return cached profile if available
    if (userProfile) return userProfile;
    
    // Otherwise fetch fresh
    return await fetchUserProfile(user.id);
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  // Enable demo mode
  const enableDemoMode = (role: string) => {
    const demoUser = {
      id: 'demo-user-' + Date.now(),
      email: `demo-${role}@example.com`,
      email_confirmed_at: new Date().toISOString()
    };
    
    const demoProfile: UserProfile = {
      id: demoUser.id,
      email: demoUser.email,
      full_name: `Demo ${role.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
      role: role,
      created_at: new Date().toISOString()
    };
    
    setIsDemoMode(true);
    setUser(demoUser);
    setUserProfile(demoProfile);
    setSession({ user: demoUser });
    setLoading(false);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    userProfile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    enableDemoMode,
    isDemoMode
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:295:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="295" data-component-end-line="295" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
